import './App.css';
import HomePage from './pages/HomePage';
import AdminPage from './pages/AdminPage';
import { BrowserRouter as Router, HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import AnalyticsPage from './pages/AnalyticsPage';
import CustomerPage from './pages/CustomerPage';
import KitchenPage from './pages/KitchenPage';
import IngredientsPage from './pages/IngredientsPage';
import SignupPage from './pages/SignupPage';
import DashboardPage from './pages/DashboardPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DevDashboard from './pages/DevDashboard';

import StaffPage from './pages/StaffPage';
import BranchesPage from './pages/BranchesPage';
import AboutPage from './pages/AboutPage';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import FAQsPage from './pages/FAQsPage';
import PaymentStatus from './pages/PaymentStatus';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { BranchProvider } from './contexts/BranchContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import ProtectedRoute from './components/ProtectedRoute';
import DebugInfo from './components/DebugInfo';
import SupportChat from './components/SupportChat';
import ScrollToTop from './components/ScrollToTop';
import ElectronBackButton from './components/ElectronBackButton';

import { useAuth } from './contexts/AuthContext';

import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import CookiePolicyPage from './pages/CookiePolicyPage';

import LoadingScreen from './components/LoadingScreen';

const RootRoute = () => {
  const { token, isLoading } = useAuth();
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // Show LoadingScreen while checking auth state
  if (isLoading) return <LoadingScreen />;

  // Check if running in Electron
  const isElectron = window.navigator.userAgent.toLowerCase().includes('electron');

  // Parse QR customer parameters
  const pathParams = new URLSearchParams(window.location.search);
  let table = pathParams.get('table');
  let companyId = pathParams.get('companyId') || pathParams.get('company_id');
  let branchId = pathParams.get('branch_id');

  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  if (qIdx !== -1) {
    const hashParams = new URLSearchParams(hash.substring(qIdx));
    if (!table) table = hashParams.get('table');
    if (!companyId) companyId = hashParams.get('companyId') || hashParams.get('company_id');
    if (!branchId) branchId = hashParams.get('branch_id');
  }

  // Fallback for some QR scanners that strip the "?" from the URL resulting in path-like parameters
  if (!companyId) {
    const match = window.location.pathname.match(/company_id=([^&/]+)/);
    if (match) companyId = match[1];
  }
  if (!branchId) {
    const match = window.location.pathname.match(/branch_id=([^&/]+)/);
    if (match) branchId = match[1];
  }
  if (!table) {
    const match = window.location.pathname.match(/table=([^&/]+)/);
    if (match) table = match[1];
  }

  // Check localStorage if not in URL
  if (!table || !companyId) {
    try {
      const stored = JSON.parse(localStorage.getItem('scanned_qr_context'));
      if (stored) {
        if (!table) table = stored.table;
        if (!companyId) companyId = stored.companyId || stored.company_id;
        if (!branchId) branchId = stored.branch_id;
      }
    } catch (e) {}
  }

  // Save to localStorage if we have it
  if ((table && companyId) || companyId === '36') {
    try {
      localStorage.setItem('scanned_qr_context', JSON.stringify({
        table: table || 'waiting',
        companyId,
        branch_id: branchId,
        timestamp: Date.now()
      }));
    } catch (e) {}
  }

  // If running in Electron desktop app
  if (isElectron) {
    if (!token) {
      console.log('[RootRoute] Electron app - Redirecting to signup/login');
      return <Navigate to="/signup?mode=login" replace />;
    }
    console.log('[RootRoute] Electron app - Rendering HomePage');
    return <HomePage />;
  }

  let isSubdomain = false;

  // Handle localhost cases
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    isSubdomain = false;
  } else if (hostname.endsWith('.localhost')) {
    isSubdomain = true;
  }
  // Handle production main domains
  else if (hostname === 'redsorm.in' || hostname === 'dev.redsorm.in' || hostname === 'www.redsorm.in' || hostname === 'www.dev.redsorm.in') {
    isSubdomain = false;
  }
  // Handle production subdomains
  else if (hostname.endsWith('.redsorm.in')) {
    isSubdomain = true;
  }
  // Handle Render, Vercel, and Cloudflare Pages/Workers deployments (all *.pages.dev and *.workers.dev are main app)
  else if (hostname.endsWith('onrender.com') || hostname.endsWith('vercel.app') || hostname.endsWith('pages.dev') || hostname.endsWith('workers.dev')) {
    isSubdomain = false;
  }
  else if (parts.length > 2 && parts[0] !== 'www') {
    isSubdomain = true;
  }

  const cleanCompanyId = companyId ? companyId.replace(/[^0-9]/g, '') : null;
  let noTable = pathParams.get('no_table') || pathParams.get('no_table_mode');
  if (qIdx !== -1) {
    const hashParams = new URLSearchParams(hash.substring(qIdx));
    if (!noTable) noTable = hashParams.get('no_table') || hashParams.get('no_table_mode');
  }

  // Save to localStorage if we have customer QR context
  if (cleanCompanyId && (table || noTable || cleanCompanyId === '36')) {
    try {
      localStorage.setItem('scanned_qr_context', JSON.stringify({
        table: table || (noTable ? 'none' : 'waiting'),
        companyId: cleanCompanyId,
        branch_id: branchId,
        no_table: noTable ? 1 : 0,
        timestamp: Date.now()
      }));
    } catch (e) {}
  }

  // Guest Customer ordering takes precedence!
  // If we have a table context, no_table parameter, or companyId parameter from QR scan, redirect directly to customer.html instead of signup/login
  if (cleanCompanyId && (table || noTable || pathParams.has('company_id') || pathParams.has('companyId') || cleanCompanyId === '36')) {
    console.log('[RootRoute] Guest customer QR session detected - Redirecting to Customer Page');
    let redirectUrl = `/customer.html?companyId=${cleanCompanyId}`;
    if (table) redirectUrl += `&table=${table}`;
    if (branchId) redirectUrl += `&branch_id=${branchId}`;
    if (noTable) redirectUrl += `&no_table=1`;
    return <Navigate to={redirectUrl} replace />;
  }

  if (isSubdomain) {
    if (!token) {
      // If unauthenticated subdomain visitor has no table/guest context, redirect to login
      console.log('[RootRoute] Unauthenticated tenant access - Redirecting to Login Page');
      return <Navigate to="/signup?mode=login" replace />;
    }
    console.log('[RootRoute] Rendering HomePage for tenant');
    return <HomePage />;
  } else {
    console.log('[RootRoute] Rendering DashboardPage (public marketing page)');
    return <DashboardPage />;
  }
};

function App() {
  console.log('[App] 🚀 Application Mounted/Updated');
  
  // Check if running in Electron
  const isElectron = window.navigator.userAgent.toLowerCase().includes('electron');
  const RouterComponent = isElectron ? HashRouter : Router;

  // Dynamically compute basename for subdirectory deployments (like /RedSorm)
  const basename = (() => {
    if (isElectron) return '';
    const pathname = window.location.pathname; // e.g. "/RedSorm/admin.html" or "/RedSorm/"
    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) return '';

    const knownRoutes = [
      'admin.html', 'analytics.html', 'kitchen.html', 'customer.html', 
      'ingredients', 'dashboard', 'signup', 'staff', 'branches', 
      'about', 'features', 'pricing', 'faqs', 'login', 'reset-password', 
      'payment', 'dev-admin', 'privacy-policy', 'terms-of-service', 'cookie-policy'
    ];

    const routeIndex = segments.findIndex(seg => knownRoutes.some(r => seg.startsWith(r)));
    if (routeIndex > 0) {
      return '/' + segments.slice(0, routeIndex).join('/');
    } else if (routeIndex === 0) {
      return '';
    }

    // Fallback: If no known route, but we have a custom folder name in pathname
    if (segments.length > 0 && !knownRoutes.some(r => segments[0].startsWith(r))) {
      return '/' + segments[0];
    }
    return '';
  })();

  console.log('[App] Router Basename:', basename || 'ROOT (/)');
  const routerProps = basename ? { basename } : {};
  
  return (
    <LanguageProvider>
      <AuthProvider>
        <BranchProvider>
          <CurrencyProvider>
          <DebugInfo />
          <RouterComponent {...routerProps}>
            <ScrollToTop />
            <ElectronBackButton />
            {/* Global Support Chat - Single Instance Only */}
            <SupportChat />
            <Routes>
              {/* Admin routes - keep as they are */}
              <Route path="/" element={<RootRoute />} />
              <Route path="/homepage" element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              } />
              <Route path="/admin.html" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
              <Route path="/analytics.html" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
              <Route path="/kitchen.html" element={<ProtectedRoute><KitchenPage /></ProtectedRoute>} />
              <Route path="/ingredients" element={<ProtectedRoute><IngredientsPage /></ProtectedRoute>} />
              {/* Dashboard is PUBLIC - marketing/landing page */}
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/staff" element={<StaffPage />} />
              <Route path="/branches" element={<ProtectedRoute><BranchesPage /></ProtectedRoute>} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/faqs" element={<FAQsPage />} />

              {/* Policy Pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsPage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />

              {/* Customer authentication flow (Removed CustomerAuthPage) */}
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/payment/status" element={<PaymentStatus />} />
              <Route path="/dev-admin" element={<DevDashboard />} />

              {/* Protected customer route */}
              <Route
                path="/customer.html"
                element={
                  <ProtectedRoute>
                    <CustomerPage />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </RouterComponent>
          </CurrencyProvider>
        </BranchProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;